import { ArrowRightIcon } from '@heroicons/react/solid'
import clsx from 'clsx'
import Link from 'next/link'
import { Contract, isSportsContract } from 'common/contract'
import { SportInfo, sportGroupIds } from 'common/sports-schedule'
import { DAY_MS } from 'common/util/time'
import { Col } from 'web/components/layout/col'
import { Row } from 'web/components/layout/row'
import { useAPIGetter } from 'web/hooks/use-api-getter'
import { useHidesBlocked } from './blocked-topics'
import { RelatedMarketRow } from './game-related-markets'

/**
 * Markets that aren't tied to a specific game: what's hot in this sport right
 * now, and the season-long futures (MVP, champion, playoff spots…).
 */
export function SportsMarketSections(props: {
  sport: SportInfo | 'all'
  className?: string
  /** Hold the fetches until the page knows which sport it is showing. */
  enabled?: boolean
}) {
  const { sport, className, enabled = true } = props
  const hides = useHidesBlocked()
  const key = sport === 'all' ? 'all' : sport.key
  const gids = (sport === 'all' ? sportGroupIds('all') : sport.groupIds).join(
    ','
  )
  const label = sport === 'all' ? 'sports' : sport.label
  const slug = sport === 'all' ? 'sports-default' : sport.slug
  // /browse reads the topic filter from `tf` and the open/resolved filter from `f`.
  const seeAllHref = slug
    ? `/browse?tf=${slug}`
    : `/browse?q=${encodeURIComponent(label)}`

  // Distinct cache slots: useAPIGetter keys its data by path, so two calls to
  // the same endpoint would otherwise overwrite each other.
  const trending = useAPIGetter(
    'search-markets-full',
    { term: '', filter: 'open', sort: 'score', gids, limit: 30 },
    undefined,
    `sports-trending-${key}`,
    enabled && gids.length > 0
  )
  const futures = useAPIGetter(
    'search-markets-full',
    { term: '', filter: 'open', sort: 'liquidity', gids, limit: 40 },
    undefined,
    `sports-futures-${key}`,
    enabled && gids.length > 0
  )

  // An empty topic set must never become an unfiltered search.
  if (!gids) return null

  const now = Date.now()
  const trendingMarkets = (trending.data ?? [])
    .filter((c) => !isSportsContract(c) && !hides(c.groupSlugs))
    .slice(0, 8)
  const trendingIds = new Set(trendingMarkets.map((c) => c.id))
  const futuresMarkets = (futures.data ?? [])
    .filter(
      (c) =>
        !isSportsContract(c) &&
        !hides(c.groupSlugs) &&
        !trendingIds.has(c.id) &&
        (c.closeTime ?? 0) - now > 21 * DAY_MS
    )
    .slice(0, 8)

  return (
    <Col className={clsx('gap-5', className)}>
      <MarketSection
        title={
          sport === 'all' ? 'Trending across sports' : `Trending in ${label}`
        }
        contracts={trendingMarkets}
        loading={trending.loading && !trending.data}
        seeAllHref={seeAllHref}
      />
      <MarketSection
        title="Futures & season-long"
        contracts={futuresMarkets}
        loading={futures.loading && !futures.data}
        seeAllHref={seeAllHref}
      />
    </Col>
  )
}

function MarketSection(props: {
  title: string
  contracts: Contract[]
  loading: boolean
  seeAllHref: string
}) {
  const { title, contracts, loading, seeAllHref } = props
  if (!loading && contracts.length === 0) return null
  return (
    <Col className="border-ink-200 bg-canvas-0 gap-1 rounded-lg border">
      <Row className="items-center justify-between px-3 pb-1 pt-3">
        <h3 className="text-ink-900 text-sm font-semibold">{title}</h3>
        <Link
          href={seeAllHref}
          className="text-ink-500 hover:text-primary-700 flex items-center gap-1 text-xs"
        >
          See all <ArrowRightIcon className="h-3 w-3" />
        </Link>
      </Row>
      {loading ? (
        <Col className="gap-2 px-3 pb-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-8 animate-pulse rounded" />
          ))}
        </Col>
      ) : (
        <Col className="divide-ink-100 divide-y pb-1">
          {contracts.map((c) => (
            <RelatedMarketRow key={c.id} contract={c} />
          ))}
        </Col>
      )}
    </Col>
  )
}
